import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  ApplicationRef,
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SessionService } from '@galaxy/core';
import { HostService } from '@vendasta/atlas';
import { Observable, skipWhile, switchMap, take } from 'rxjs';
import { AtlasConfigService, AtlasDataService } from '../../../core/src';
import { ModalService } from '../core-components/modal/modal.service';
import { SnackbarComponent } from '../core-components/snackbar/snackbar.component';
import { EmailVerificationComponent } from '../email-verification/email-verification.component';
import { SettingsComponent } from '../notifications/settings/settings.component';
import { AtlasThemingService } from '../theming.service';
import { AtlasTopBarComponent } from '../top-bar/top-bar.component';
import { AtlasTopBarService } from '../top-bar/top-bar.service';
import { CustomUserDropdownItem } from '../user-dropdown/user-dropdown.interface';

const MODAL_CONTAINER_ID = 'atlas-navbar-modal-container';

@Component({
  selector: 'atlas-navbar',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.scss', '../common.scss'],
})
export class NavComponent implements OnInit, OnChanges, OnDestroy {
  @Input() accountId?: string;
  @Input() partnerId?: string;
  @Input() marketId?: string;
  @Input() groupPath?: string;
  @Input() signOutNextUrl?: string;
  @Input() serviceProviderId: string;
  @Input() dropdownItems?: string | CustomUserDropdownItem[];
  @Input() hideCenters?: string | boolean;
  @Input() hideNotifications?: string | boolean;
  @Input() showLanguageSelector?: string | boolean;
  @Input() disableUserDropdown?: string | boolean;
  @Output() event: EventEmitter<string> = new EventEmitter<string>();

  private topbar: ComponentRef<AtlasTopBarComponent>;
  private snackbar: ComponentRef<SnackbarComponent>;

  constructor(
    private cfgService: AtlasConfigService,
    public apiService: AtlasDataService,
    private sessionService: SessionService,
    private appRef: ApplicationRef,
    private topbarService: AtlasTopBarService,
    private themingService: AtlasThemingService,
    private http: HttpClient,
    private hostService: HostService,
    private modalService: ModalService,
  ) {
    // Set up theming
    apiService.theming$.pipe(takeUntilDestroyed()).subscribe((theming) => {
      this.themingService.theming = theming;
    });
  }

  ngOnInit(): void {
    document.querySelector('body').style.setProperty('padding-top', '40px');

    this.parseDropdownItems();
    this.calculateHideCenters();
    this.calculateHideNotifications();
    this.calculateShowLanguageSelector();
    this.refreshConfig();

    if (window.location.search.indexOf(SettingsComponent.SETTINGS_DIALOG_PARAM_ID) >= 0) {
      this.modalService.openList(SettingsComponent);
    }

    this.getShouldShowEmailVerificationInterstitial().subscribe((resp) => {
      if (resp.display) {
        this.modalService.openFullscreen(EmailVerificationComponent);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.dropdownItems) {
      this.parseDropdownItems();
    }
    if (changes.hideCenters) {
      this.calculateHideCenters();
    }
    if (changes.hideNotifications) {
      this.calculateHideNotifications();
    }
    if (changes.disableUserDropdown) {
      this.calculateHideUserDropdown();
    }
    if (changes.showLanguageSelector) {
      this.calculateShowLanguageSelector();
    }
    if (
      !!changes.partnerId ||
      !!changes.marketId ||
      !!changes.accountId ||
      !!changes.serviceProviderId ||
      !!changes.signOutNextUrl ||
      !!changes.groupPath
    ) {
      this.refreshConfig();
    }
  }

  ngOnDestroy(): void {
    if (this.topbar) {
      this.topbar.destroy();
      this.appRef.detachView(this.topbar.hostView);
    }

    if (this.snackbar) {
      this.snackbar.destroy();
      this.appRef.detachView(this.snackbar.hostView);
    }

    const modalContainer = document.getElementById(MODAL_CONTAINER_ID);
    if (modalContainer) {
      modalContainer.remove();
    }
  }

  propagateEvent(eventId: string): void {
    this.event.emit(eventId);
  }

  private parseDropdownItems(): void {
    if (typeof this.dropdownItems === 'object') {
      this.topbarService.setDropdownItems(this.dropdownItems as CustomUserDropdownItem[]);
      return;
    }
    if (!this.dropdownItems) {
      return;
    }
    try {
      const parsedItems: any = JSON.parse(this.dropdownItems);
      const dropdownItems: CustomUserDropdownItem[] = [];
      for (const item of parsedItems) {
        dropdownItems.push({
          label: item.label,
          url: item.url,
          eventId: item.eventId,
        });
      }
      this.topbarService.setDropdownItems(dropdownItems);
    } catch (e) {
      console.log('Failed to parse Dropdown Items. ' + e.message);
    }
  }

  private calculateHideCenters(): void {
    this.topbarService.setCenterVisibility(!(this.hideCenters === 'true' || this.hideCenters === true));
  }

  private calculateHideNotifications(): void {
    this.topbarService.setNotificationsVisibility(
      !(this.hideNotifications === 'true' || this.hideNotifications === true),
    );
  }

  private calculateHideUserDropdown(): void {
    this.topbarService.setUserDropdownVisibility(
      !(this.disableUserDropdown === 'true' || this.disableUserDropdown === true),
    );
  }

  private calculateShowLanguageSelector(): void {
    this.topbarService.setLanguageSelectorVisibility(
      this.showLanguageSelector === 'true' || this.showLanguageSelector === true,
    );
  }

  private refreshConfig(): void {
    this.cfgService.initialize(
      this.serviceProviderId,
      this.partnerId,
      this.marketId,
      this.accountId,
      this.serviceProviderId,
      this.signOutNextUrl,
      this.groupPath,
    );
  }

  getShouldShowEmailVerificationInterstitial(): Observable<{ display: boolean }> {
    const shouldDisplay = this.http.post<{ display: boolean }>(
      this.hostService.hostWithScheme + '/to-display-email-verification',
      null,
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
        withCredentials: true,
      },
    );
    return this.sessionService.getSessionId().pipe(
      skipWhile((sess) => !sess),
      switchMap(() => shouldDisplay),
      take(1),
    );
  }
}
